/**
 * ExtractDocSidebar — the parsed viewer's document list, in the Document
 * Coverage shape (ported from tpo-portal-frontend): a card per document group,
 * a row per document type with its count, and the parsed files under each
 * type. Group and type come from the source document's LIVE classification
 * (`parent`), falling back to the manifest provenance; anything without one
 * files under "Uncategorized".
 *
 * The type holding the open document starts expanded (and re-expands when the
 * pager moves to another type); a search opens every type it matches.
 */
import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CloseRounded from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import UnfoldLessRounded from "@mui/icons-material/UnfoldLessRounded";
import UnfoldMoreRounded from "@mui/icons-material/UnfoldMoreRounded";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import colors, { teal } from "../../../../../theme/colors";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import { documentStatusLabel } from "../../../../../constants/selectOptions/selectOptionsDocumentStatus";
import { getStatusStyle } from "../../../../../components/agGrid/cellRenderer/LoanFileStatusCell";

const LINE = colors.slate200;
const LINE_SOFT = colors.slate100;
const CARD_SHADOW = "0 1px 2px rgba(15,23,42,.05)";

export const extractParentOf = (d) => d?.parent || d?.parent_provenance || {};

// The live source doc when there is one, else the manifest provenance — the
// same precedence the index itself sorts by.
export const extractNameOf = (d) => {
  const live = d?.parent || {};
  const prov = d?.parent_provenance || {};
  return live.file_display_name || live.name || prov.name || "Untitled document";
};

export const extractGroupOf = (d) =>
  d?.parent?.docGroup || d?.parent_provenance?.docGroup || null;

export const extractTypeOf = (d) =>
  d?.parent?.docType || d?.parent_provenance?.docType || null;

const keyOf = (group, type) => `${group}|${type}`;

// The search term, marked inside a label.
const Highlight = ({ text, term }) => {
  const value = String(text ?? "");
  if (!term) return value;
  const idx = value.toLowerCase().indexOf(term);
  if (idx < 0) return value;
  return (
    <>
      {value.slice(0, idx)}
      <Box
        component="mark"
        sx={{ background: teal[100], color: "inherit", borderRadius: "3px", px: "1px" }}
      >
        {value.slice(idx, idx + term.length)}
      </Box>
      {value.slice(idx + term.length)}
    </>
  );
};

const TOOLTIP_EYEBROW = {
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  opacity: 0.7,
  mb: "2px",
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// One parsed file under its type: status dot, name, upload date. The dot and
// name are one hover target carrying everything a status pill would say.
const FileRow = ({ item, term, active, onSelect }) => {
  const status = item.doc?.documentStatus;
  const uploaded = item.doc?.uploadDate;
  const counts = item.entry?.counts || {};
  const artifacts = [
    counts.structured_json ? "structured" : null,
    counts.markdown ? "text" : null,
    counts.image ? plural(counts.image, "image") : null,
  ].filter(Boolean);
  const facts = [
    status ? `Status: ${documentStatusLabel(status)}` : null,
    artifacts.length ? `Parsed: ${artifacts.join(" · ")}` : null,
    uploaded ? `Uploaded: ${formatDateTime(uploaded, "datetime")}` : null,
    item.entry?.source_kind === "agent_run"
      ? "Agent run report"
      : item.entry?.source_kind === "generated"
        ? "Generated — no source document"
        : null,
  ].filter(Boolean);
  return (
    <ButtonBase
      onClick={() => onSelect(item.id)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        textAlign: "left",
        px: 0.75,
        py: 0.625,
        borderRadius: 0,
        backgroundColor: active ? teal[50] : "transparent",
        boxShadow: active ? `inset 2px 0 0 ${teal[500]}` : "none",
        "&:hover": { backgroundColor: active ? teal[50] : LINE_SOFT },
      }}
    >
      <Tooltip
        placement="right"
        title={
          <Box sx={{ py: 0.25 }}>
            <Box sx={TOOLTIP_EYEBROW}>Parsed document</Box>
            <Box sx={{ fontSize: 11.5, fontWeight: 500 }}>{item.name}</Box>
            {facts.map((line) => (
              <Box key={line} sx={{ fontSize: 10.5, opacity: 0.85 }}>
                {line}
              </Box>
            ))}
          </Box>
        }
      >
        {/* Box, not Flx — the Tooltip needs a child that forwards its ref. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              flex: "none",
              backgroundColor: status ? getStatusStyle(status).dot : colors.slate300,
            }}
          />
          <Txt
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 10.5,
              fontWeight: active ? 500 : 400,
              color: active ? colors.slate900 : colors.slate700,
            }}
          >
            <Highlight text={item.name} term={term} />
          </Txt>
        </Box>
      </Tooltip>
      {uploaded ? (
        <Txt sx={{ flex: "none", fontSize: 10, color: colors.slate400 }}>
          {formatDateTime(uploaded, "M/d/yy")}
        </Txt>
      ) : null}
    </ButtonBase>
  );
};

// A document type: dot, name, file count, chevron; opens to its files.
const TypeRow = ({ type, term, open, onToggle, selectedId, onSelect }) => {
  const holdsSelected = type.items.some((i) => i.id === selectedId);
  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.125,
          px: 1,
          py: 0.75,
          borderRadius: "7px",
          cursor: "pointer",
          outline: "none",
          "&:hover": { backgroundColor: teal[50] },
          "&:focus-visible": { boxShadow: `0 0 0 2px ${teal[300]}` },
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            flex: "none",
            backgroundColor: teal[500],
          }}
        />
        <Tooltip title={type.label} enterDelay={800}>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 11,
              fontWeight: holdsSelected ? 500 : 400,
              color: colors.slate900,
            }}
          >
            <Highlight text={type.label} term={term} />
          </Box>
        </Tooltip>
        <Box
          component="span"
          sx={{
            flex: "none",
            fontSize: 10,
            fontWeight: 600,
            color: teal[700],
            backgroundColor: teal[100],
            borderRadius: "999px",
            px: 1,
            py: "1px",
          }}
        >
          {type.items.length}
        </Box>
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 14,
            color: colors.slate400,
            flex: "none",
            transition: "transform .15s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>
      {open && (
        <Box sx={{ ml: 2.25, pl: 1, borderLeft: `1px solid ${LINE}`, my: 0.25 }}>
          {type.items.map((item) => (
            <FileRow
              key={item.id}
              item={item}
              term={term}
              active={item.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// A document group as a card: name, file count, collapsible body of types.
const GroupCard = ({
  group,
  term,
  collapsed,
  onToggleCollapsed,
  openTypes,
  onToggleType,
  selectedId,
  onSelect,
}) => (
  <Box
    sx={{
      backgroundColor: colors.white,
      border: `1px solid ${LINE}`,
      borderRadius: "10px",
      boxShadow: CARD_SHADOW,
      mb: 1.25,
      overflow: "hidden",
    }}
  >
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={!collapsed}
      onClick={onToggleCollapsed}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleCollapsed();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1.125,
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        "&:hover": { backgroundColor: colors.slate50 },
        "&:focus-visible": { boxShadow: `inset 0 0 0 2px ${teal[300]}` },
      }}
    >
      <Txt sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 500, color: colors.slate900 }}>
        <Highlight text={group.name} term={term} />
      </Txt>
      <Box
        component="span"
        sx={{
          flex: "none",
          fontSize: 10.5,
          fontWeight: 500,
          borderRadius: "999px",
          px: 1.125,
          py: "2px",
          color: teal[700],
          backgroundColor: teal[100],
        }}
      >
        {plural(group.count, "file")}
      </Box>
      <KeyboardArrowDownRounded
        sx={{
          fontSize: 18,
          color: colors.slate400,
          flex: "none",
          transition: "transform .15s",
          transform: collapsed ? "rotate(-90deg)" : "none",
        }}
      />
    </Box>
    {!collapsed && (
      <Box
        sx={{
          pt: 0.5,
          pb: 1,
          px: 0.5,
          // A hairline under the header, inset to the card's text edge, so the
          // header reads as a heading over its list rather than one block.
          "&::before": {
            content: '""',
            display: "block",
            height: "1px",
            mx: 1,
            mb: 0.5,
            backgroundColor: LINE_SOFT,
          },
        }}
      >
        {group.types.map((type) => (
          <TypeRow
            key={type.key}
            type={type}
            term={term}
            open={openTypes.has(type.key)}
            onToggle={() => onToggleType(type.key)}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
      </Box>
    )}
  </Box>
);

export default function ExtractDocSidebar({ documents, selectedId, onSelectId, width }) {
  const [query, setQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [openTypes, setOpenTypes] = useState(() => new Set());

  const model = useMemo(() => {
    const groups = new Map();
    for (const entry of documents || []) {
      const id = entry?.parent_document_id;
      if (!id) continue;
      const groupName = extractGroupOf(entry) || "Uncategorized";
      const typeName = extractTypeOf(entry) || "Uncategorized";
      if (!groups.has(groupName)) groups.set(groupName, new Map());
      const types = groups.get(groupName);
      if (!types.has(typeName)) types.set(typeName, []);
      types.get(typeName).push({
        id,
        entry,
        doc: entry.parent || null,
        name: extractNameOf(entry),
      });
    }
    return [...groups.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, types]) => {
        const typeList = [...types.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([typeName, items]) => ({
            key: keyOf(name, typeName),
            label: typeName,
            items,
          }));
        return {
          name,
          types: typeList,
          count: typeList.reduce((n, t) => n + t.items.length, 0),
        };
      });
  }, [documents]);

  // Open the type holding the selected document, so the pager and a click
  // from the table both land with the file in view.
  useEffect(() => {
    if (!selectedId) return;
    const group = model.find((g) =>
      g.types.some((t) => t.items.some((i) => i.id === selectedId)),
    );
    const type = group?.types.find((t) => t.items.some((i) => i.id === selectedId));
    if (!group || !type) return;
    setOpenTypes((prev) => (prev.has(type.key) ? prev : new Set(prev).add(type.key)));
    setCollapsedGroups((prev) => {
      if (!prev.has(group.name)) return prev;
      const next = new Set(prev);
      next.delete(group.name);
      return next;
    });
  }, [selectedId, model]);

  const term = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!term) return model;
    return model
      .map((g) => {
        const groupHit = g.name.toLowerCase().includes(term);
        const types = g.types
          .map((t) => {
            const typeHit = t.label.toLowerCase().includes(term);
            const items =
              groupHit || typeHit
                ? t.items
                : t.items.filter((i) => i.name.toLowerCase().includes(term));
            return items.length ? { ...t, items } : null;
          })
          .filter(Boolean);
        return types.length
          ? { ...g, types, count: types.reduce((n, t) => n + t.items.length, 0) }
          : null;
      })
      .filter(Boolean);
  }, [model, term]);

  const toggleGroup = (name) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const toggleType = (key) =>
    setOpenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const expandAll = () => {
    setCollapsedGroups(new Set());
    setOpenTypes(new Set(model.flatMap((g) => g.types.map((t) => t.key))));
  };
  const collapseAll = () => {
    setCollapsedGroups(new Set(model.map((g) => g.name)));
    setOpenTypes(new Set());
  };

  const total = (documents || []).length;

  return (
    <Flx
      column
      sx={{
        width,
        flex: "0 0 auto",
        borderRight: `1px solid ${LINE}`,
        backgroundColor: colors.slate50,
        minHeight: 0,
      }}
    >
      <Flx
        column
        g={1}
        sx={{ p: 1.5, pb: 1.25, flexShrink: 0, borderBottom: `1px solid ${LINE_SOFT}` }}
      >
        <Flx ac jb>
          <Flx ac g={0.75}>
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: colors.slate900 }}>
              Parsed documents
            </Txt>
            <Txt sx={{ fontSize: 11.5, color: colors.slate500 }}>{total}</Txt>
          </Flx>
          <Flx ac g={0.25}>
            <Tooltip title="Expand all">
              <IconButton size="small" onClick={expandAll} sx={{ color: colors.slate500 }}>
                <UnfoldMoreRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse all">
              <IconButton size="small" onClick={collapseAll} sx={{ color: colors.slate500 }}>
                <UnfoldLessRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Flx>
        </Flx>
        {/* A lone search box — no form to align to, so no label above it. */}
        <TextField
          size="small"
          placeholder="Search groups, types & files…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          sx={{
            "& .MuiOutlinedInput-root": { backgroundColor: colors.white, fontSize: 12 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 16, color: colors.slate400 }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                  sx={{ p: 0.25 }}
                >
                  <CloseRounded sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Flx>
      <Box sx={{ flex: 1, overflowY: "auto", p: 1.25, minHeight: 0 }}>
        {visible.length === 0 ? (
          <Txt
            component="div"
            sx={{ p: 3.5, textAlign: "center", color: colors.slate500, fontSize: 12 }}
          >
            {total ? "No documents match your search." : "No parsed documents yet."}
          </Txt>
        ) : (
          visible.map((group) => (
            <GroupCard
              key={group.name}
              group={group}
              term={term}
              // A search shows every match, whatever was collapsed before it.
              collapsed={!term && collapsedGroups.has(group.name)}
              onToggleCollapsed={() => toggleGroup(group.name)}
              openTypes={term ? new Set(group.types.map((t) => t.key)) : openTypes}
              onToggleType={toggleType}
              selectedId={selectedId}
              onSelect={onSelectId}
            />
          ))
        )}
      </Box>
    </Flx>
  );
}
